import CryptoJS from "crypto-js";
import { ZIM, ZIMSDK } from "zego-zim-web";

const appConfig = {
  appID: 846753220,
  serverSecret: "27933668fde2ea2320b24c7663ac58cf",
};

export const avatarPrefix = "https://storage.zego.im/zim/example/web/assets/";

/** True when IndexedDB can open a short-lived probe DB. */
function canUseIndexedDb(): boolean {
  try {
    if (typeof indexedDB === "undefined") return false;
    // Sync availability only — async open failures are handled by callers.
    return true;
  } catch {
    return false;
  }
}

let zimInstance: ZIMSDK | null = null;
let zimInitFailed = false;

/**
 * Lazily create Zego ZIM. Never throws — returns null when storage/IndexedDB
 * is unavailable (e.g. "Database deleted by request of the user").
 */
export function getZim(): ZIMSDK | null {
  if (zimInstance) return zimInstance;
  if (zimInitFailed) return null;
  if (!canUseIndexedDb()) {
    zimInitFailed = true;
    console.warn("Zego ZIM skipped: IndexedDB unavailable");
    return null;
  }

  try {
    const created = ZIM.create(appConfig);
    zimInstance = created || ZIM.getInstance();
    // @ts-ignore — debug helper used by existing chat tooling
    window.zim = zimInstance;
    return zimInstance;
  } catch (error) {
    zimInitFailed = true;
    console.warn("Zego ZIM init skipped:", error);
    return null;
  }
}

/** @deprecated Prefer getZim() — may be null when storage is blocked. */
export const zim = new Proxy({} as ZIMSDK, {
  get(_target, prop, receiver) {
    const instance = getZim();
    if (!instance) {
      if (prop === "login") {
        return () =>
          Promise.reject(new Error("Zego ZIM unavailable in this browser"));
      }
      return undefined;
    }
    const value = Reflect.get(instance as object, prop, receiver);
    return typeof value === "function" ? value.bind(instance) : value;
  },
});

export const SDKVersion = (() => {
  try {
    return ZIM.getVersion();
  } catch {
    return "unavailable";
  }
})();

export function generateToken(userID: string, seconds: number): string {
  if (!userID) throw new Error("generateToken error: params invalid.");

  const time = (Date.now() / 1000) | 0;
  const body = {
    app_id: Number(appConfig.appID),
    user_id: String(userID),
    nonce: (Math.random() * 2147483647) | 0,
    ctime: time,
    expire: time + Number(seconds || 7200),
  };

  const key = CryptoJS.enc.Utf8.parse(appConfig.serverSecret);
  let iv = Math.random().toString().substring(2, 18);
  if (iv.length < 16) iv += iv.substring(0, 16 - iv.length);

  const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(body), key, {
    iv: CryptoJS.enc.Utf8.parse(iv),
  }).toString();
  const ciphert = Uint8Array.from(
    Array.from(atob(ciphertext)).map((val) => val.charCodeAt(0))
  );
  const len_ciphert = ciphert.length;

  const uint8 = new Uint8Array(8 + 2 + 16 + 2 + len_ciphert);
  uint8.set([0, 0, 0, 0]);
  uint8.set(new Uint8Array(Int32Array.from([body.expire]).buffer).reverse(), 4);
  uint8[8] = iv.length >> 8;
  uint8[9] = iv.length - (uint8[8] << 8);
  uint8.set(
    Uint8Array.from(Array.from(iv).map((val) => val.charCodeAt(0))),
    10
  );
  uint8[26] = len_ciphert >> 8;
  uint8[27] = len_ciphert - (uint8[26] << 8);
  uint8.set(ciphert, 28);

  const token = `04${btoa(String.fromCharCode(...Array.from(uint8)))}`;
  localStorage.setItem("zego-token", token);
  return token;
}

/** Login helper that no-ops safely when ZIM/storage is unavailable. */
export async function zimLoginSafe(
  user: { userID: string; userName: string },
  token: string
): Promise<boolean> {
  const instance = getZim();
  if (!instance) return false;
  try {
    await instance.login(user, token);
    return true;
  } catch (error) {
    console.warn("Zego ZIM login skipped:", error);
    return false;
  }
}
